/**
 * lib/pult-zoom.ts — the window onto the board: how far it is spread and where it stands (task 54).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Лупа и увеличение». The board fits the
 * screen whole, and on a phone every control of it is smaller than a finger; after the magnifier
 * two fingers spread the picture and one moves it. This is the arithmetic of that, with no
 * browser in it: pointers in, a window out. The screen (`screens/Pult.tsx`) feeds it the
 * pointers of its gesture layer and draws the window with {@link boardTransform}.
 *
 * ## The window
 *
 * `scale` — 1 is the whole board as it fits the screen, up to {@link MAX_SCALE}. `x`, `y` — where
 * the board's centre stands off the stage's centre, in shares of the board as it fits (its width,
 * its height): a window means the same part of the board on a phone of another size, so the one
 * the phone remembers still fits after a rotation of the same orientation or a new phone.
 *
 * ## The limits
 *
 * The board's edge may come into the screen no further than its middle: whatever the fingers do,
 * the centre of the screen is over the board, so no window is ever an empty black screen. In
 * shares that is `|x| ≤ scale / 2`, and the same for `y`.
 *
 * ## Pinch about the fingers
 *
 * The point of the board between the two fingers when they came down stays between them as they
 * move: the picture is held, not scaled about some centre of its own. One finger moves the board
 * by as much as it moved. A finger lifted or added starts the next stretch from the window as it
 * is, so lifting one of two fingers does not jump the picture.
 */

/** The window onto the board — see the header. */
export interface PultWindow {
  readonly scale: number;
  readonly x: number;
  readonly y: number;
}

/** A point on the stage, in its own points from its top-left corner. */
export interface PultPoint {
  readonly x: number;
  readonly y: number;
}

/** The stage, and the board as it fits there at scale 1. */
export interface PultFrame {
  readonly width: number;
  readonly height: number;
  readonly boardWidth: number;
  readonly boardHeight: number;
}

/** The whole board, centred. */
export const WHOLE: PultWindow = Object.freeze({ scale: 1, x: 0, y: 0 });

/** As far as two fingers spread it, from the whole board. */
export const MAX_SCALE = 4;

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** `w` within the limits of the header; a window that is not numbers is the whole board. */
export function clampWindow(w: PultWindow): PultWindow {
  if (![w.scale, w.x, w.y].every(Number.isFinite)) return WHOLE;
  const scale = clamp(w.scale, 1, MAX_SCALE);
  const reach = scale / 2;
  // `+ 0` turns a clamped −0 into 0: a stored window reads back as the one written.
  return { scale, x: clamp(w.x, -reach, reach) + 0, y: clamp(w.y, -reach, reach) + 0 };
}

/** A value read off the phone as a window, or `undefined` when it is not one. */
export function asWindow(value: unknown): PultWindow | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { scale, x, y } = value as Record<string, unknown>;
  if (typeof scale !== 'number' || typeof x !== 'number' || typeof y !== 'number') return undefined;
  if (![scale, x, y].every(Number.isFinite)) return undefined;
  return clampWindow({ scale, x, y });
}

/** One finger moved from `from` to `to`: the board goes with it. */
export function pan(
  start: PultWindow,
  from: PultPoint,
  to: PultPoint,
  frame: PultFrame,
): PultWindow {
  if (frame.boardWidth <= 0 || frame.boardHeight <= 0) return start;
  return clampWindow({
    scale: start.scale,
    x: start.x + (to.x - from.x) / frame.boardWidth,
    y: start.y + (to.y - from.y) / frame.boardHeight,
  });
}

function distance(a: PultPoint, b: PultPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Two fingers moved from `from` to `to`: spread about the point between them. */
export function pinch(
  start: PultWindow,
  from: readonly [PultPoint, PultPoint],
  to: readonly [PultPoint, PultPoint],
  frame: PultFrame,
): PultWindow {
  const { boardWidth: bw, boardHeight: bh } = frame;
  if (bw <= 0 || bh <= 0) return start;
  const mid = (pair: readonly [PultPoint, PultPoint]): PultPoint => ({
    x: (pair[0].x + pair[1].x) / 2 - frame.width / 2,
    y: (pair[0].y + pair[1].y) / 2 - frame.height / 2,
  });
  const was = distance(from[0], from[1]);
  // Two fingers on one point say nothing of a spread: they move the board, as one would.
  const ratio = was < 1 ? 1 : distance(to[0], to[1]) / was;
  const scale = clamp(start.scale * ratio, 1, MAX_SCALE);
  const m0 = mid(from);
  const m1 = mid(to);
  // The board's point under the first midpoint, from its centre, unscaled …
  const bx = (m0.x - start.x * bw) / start.scale;
  const by = (m0.y - start.y * bh) / start.scale;
  // … stands under the midpoint now.
  return clampWindow({ scale, x: (m1.x - scale * bx) / bw, y: (m1.y - scale * by) / bh });
}

/** The board's CSS transform for a window: about its own centre, as it fits. */
export function boardTransform(w: PultWindow): string {
  if (w.scale === 1 && w.x === 0 && w.y === 0) return 'none';
  const pct = (share: number): string => `${String(Math.round(share * 10_000) / 100 + 0)}%`;
  return `translate(${pct(w.x)}, ${pct(w.y)}) scale(${String(Math.round(w.scale * 1000) / 1000)})`;
}

/** The pointers of the gesture layer, as they come. */
export interface ZoomTracker {
  /** A finger came down. `current` is the window as it is now: the next stretch starts there. */
  down(pointerId: number, at: PultPoint, current: PultWindow): void;
  /** A finger moved: the new window, or `undefined` when this finger moves nothing. */
  move(pointerId: number, at: PultPoint, frame: PultFrame): PultWindow | undefined;
  /** A finger left the glass, or the system took it. */
  up(pointerId: number, current: PultWindow): void;
}

export function createZoomTracker(): ZoomTracker {
  /** Where each finger is now, in the order they came down. */
  const now = new Map<number, PultPoint>();
  /** Where each finger was when the stretch began, and the window then. */
  let anchors = new Map<number, PultPoint>();
  let start: PultWindow = WHOLE;

  const anchor = (current: PultWindow): void => {
    anchors = new Map(now);
    start = current;
  };

  return {
    down(pointerId, at, current) {
      now.set(pointerId, at);
      anchor(current);
    },

    move(pointerId, at, frame) {
      if (!now.has(pointerId)) return undefined;
      now.set(pointerId, at);
      const ids = [...now.keys()];
      // The first two fingers hold the picture; a third rests on the glass and changes nothing.
      if (!ids.slice(0, 2).includes(pointerId)) return undefined;
      const [first, second] = ids;
      if (first === undefined) return undefined;
      const a0 = anchors.get(first);
      const a1 = now.get(first);
      if (a0 === undefined || a1 === undefined) return undefined;
      if (second === undefined) return pan(start, a0, a1, frame);
      const b0 = anchors.get(second);
      const b1 = now.get(second);
      if (b0 === undefined || b1 === undefined) return undefined;
      return pinch(start, [a0, b0], [a1, b1], frame);
    },

    up(pointerId, current) {
      if (!now.delete(pointerId)) return;
      anchor(current);
    },
  };
}
